import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const AdminHome = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const secondaryTools = [
    { id: 'residential-calculator', title: 'Residential Calculator', desc: 'Calculate bin requirements and waste room area for developments', route: '/admin/residential-calculator', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
    { id: 'enquiries', title: 'Enquiries', desc: 'View and manage customer enquiries', route: '/admin/enquiries', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { id: 'nsw-councils', title: 'NSW Councils', desc: 'DCP parameters for 33 NSW councils', route: '/admin/nsw-councils', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { id: 'users', title: 'User Management', desc: 'Registered users and accounts', route: '/admin/users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { id: 'generation-rates', title: 'Generation Rates', desc: 'Manage residential waste generation rates by council', route: '/admin/calculator-rates', icon: 'M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3zm3 2h10M7 13h10M7 17h4' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-40" data-testid="admin-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-14">
          <Link to="/" className="text-2xl font-bold tracking-tight text-[#0A2540]">
            PlanGen<span className="text-blue-600">Consulting</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-sm text-slate-500">{user?.email}</span>
            <button onClick={() => navigate('/dashboard')} className="text-xs text-slate-500 hover:text-[#0A2540] transition-colors">User Dashboard</button>
            <button onClick={() => { logout(); navigate('/login'); }} className="text-xs text-slate-400 hover:text-red-500 transition-colors">Logout</button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-[#0A2540]" data-testid="admin-title">Admin Tools</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage invoices, enquiries, councils, and users</p>
        </div>

        {/* Primary: Invoice Generator */}
        <div
          onClick={() => navigate('/admin/invoices')}
          className="group relative bg-gradient-to-b from-[#0A2540] via-[#0f3460] to-[#1a4a7a] rounded-2xl p-7 cursor-pointer hover:shadow-[0_12px_40px_rgb(10,37,64,0.3)] transition-all duration-300 overflow-hidden mb-5"
          data-testid="admin-tool-invoices"
        >
          {/* Texture */}
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
          <div className="absolute top-0 right-0 w-[250px] h-[250px] bg-blue-400/8 rounded-full blur-[80px]" />
          <div className="absolute bottom-0 left-[20%] w-[200px] h-[150px] bg-sky-400/6 rounded-full blur-[60px]" />
          <div className="relative flex items-center gap-5">
            <div className="w-14 h-14 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white mb-1">Invoice Generator</h2>
              <p className="text-sm text-slate-300">Create, edit, duplicate, and manage tax invoices with GST toggle</p>
            </div>
            <svg className="w-5 h-5 text-white/40 ml-auto flex-shrink-0 group-hover:translate-x-1 group-hover:text-white/70 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        {/* Secondary: Tool cards — horizontal row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {secondaryTools.map((tool) => (
            <div
              key={tool.id}
              onClick={() => tool.route !== '#' && navigate(tool.route)}
              className={`group bg-white rounded-xl border border-slate-100 p-4 transition-all duration-200 ${tool.comingSoon ? 'opacity-60 cursor-default' : 'cursor-pointer hover:shadow-[0_4px_20px_rgb(0,0,0,0.06)] hover:border-blue-200'}`}
              data-testid={`admin-tool-${tool.id}`}
            >
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
                <svg className="w-[18px] h-[18px] text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={tool.icon} />
                </svg>
              </div>
              <h3 className="text-sm font-bold text-[#0A2540] mb-0.5 group-hover:text-blue-700 transition-colors">{tool.title}</h3>
              <p className="text-[11px] text-slate-500 leading-relaxed">{tool.desc}</p>
              {tool.comingSoon && (
                <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">Coming Soon</span>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default AdminHome;
